import { useState } from "react";
import { CheckCircle2, Download, Mail, Phone, ChevronRight } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "../hooks/use-toast";

export default function Membership() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    branch: "",
    memberType: "veteran",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: "Missing information", description: "Please fill in name and email." });
      return;
    }
    // Save locally as mock
    const prior = JSON.parse(localStorage.getItem("vva240_membership") || "[]");
    localStorage.setItem("vva240_membership", JSON.stringify([...prior, { ...form, ts: Date.now() }]));
    setSubmitted(true);
    toast({
      title: "Application received",
      description: "Thank you for reaching out. Chapter 240 will be in touch soon.",
    });
  };

  return (
    <main className="pt-24">
      {/* Hero */}
      <section className="relative py-24 px-6 lg:px-10 bg-[#0A3161] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1773434032753-4ab4b4f1f1d3"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A3161]/60 to-[#0A3161]" />
        </div>
        <div className="relative max-w-5xl mx-auto text-center">
          <SectionHeading
            align="center"
            light
            eyebrow="Membership"
            title="Answer The Call. Stand With Chapter 240."
            description="Vietnam veterans, veterans of every era, and those who love and support them. There is a place for you here."
          />
        </div>
      </section>

      {/* Membership benefits + form */}
      <section className="py-24 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="font-condensed uppercase tracking-[0.3em] text-xs text-[#B22234]">
              Why Join?
            </p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl font-bold text-[#0A3161] leading-tight">
              Brotherhood, Purpose, Legacy.
            </h2>
            <p className="mt-6 text-[#1F2937] leading-relaxed">
              Members are the heartbeat of Chapter 240. Your voice, your vote and your presence shape the future of veteran services in Sumner County.
            </p>

            <ul className="mt-8 space-y-4">
              {[
                "Monthly meetings with dinner at VFW Post #9851",
                "Vote on chapter initiatives and leadership",
                "Access to veteran advocacy and support resources",
                "Direct involvement in tournaments and community events",
                "Camaraderie with brothers and sisters who served",
              ].map((b, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#B22234] mt-0.5 shrink-0" />
                  <span className="text-[#0A3161]">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 border-l-4 border-[#B22234] pl-6 bg-[#EEF1F6] py-6 pr-6">
              <p className="font-display italic text-xl text-[#0A3161] leading-snug">
                &ldquo;Never again will one generation of veterans abandon another.&rdquo;
              </p>
              <p className="mt-2 font-condensed uppercase tracking-widest text-xs text-[#B22234]">
                Our National Motto  |  Lived Locally
              </p>
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="https://vva240.org/application_form-2016.pdf"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-[#0A3161] text-white hover:bg-[#B22234] px-7 h-12 font-condensed uppercase tracking-widest text-xs transition-colors"
              >
                <Download className="w-4 h-4" /> Official PDF Application
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white border border-[#D9DDE3] p-8 md:p-10">
              <p className="font-condensed uppercase tracking-[0.3em] text-xs text-[#B22234] mb-2">
                Interest Form
              </p>
              <h3 className="font-display text-3xl md:text-4xl font-bold text-[#0A3161]">
                Tell Us About You
              </h3>
              <p className="mt-3 text-[#1F2937]">
                Complete the form and a chapter representative will contact you with next steps.
              </p>

              {submitted ? (
                <div className="mt-10 text-center py-16 border-2 border-dashed border-[#FFFFFF]">
                  <CheckCircle2 className="w-16 h-16 text-[#B22234] mx-auto" />
                  <h4 className="mt-4 font-display text-3xl font-bold text-[#0A3161]">Welcome Home.</h4>
                  <p className="mt-3 text-[#1F2937] max-w-md mx-auto">
                    Thank you for reaching out to Chapter 240. A member of our team will contact you soon. In the meantime, join us the first Monday of the month at VFW Post #9851.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-8 inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-xs text-[#B22234] hover:gap-4 transition-all"
                  >
                    Submit Another Response <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="mt-8 space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="font-condensed uppercase tracking-widest text-xs text-[#0A3161]">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="mt-2 h-12 rounded-none border-[#D9DDE3] focus:border-[#B22234] focus:ring-0"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="font-condensed uppercase tracking-widest text-xs text-[#0A3161]">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="mt-2 h-12 rounded-none border-[#D9DDE3] focus:border-[#B22234] focus:ring-0"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="font-condensed uppercase tracking-widest text-xs text-[#0A3161]">
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="mt-2 h-12 rounded-none border-[#D9DDE3] focus:border-[#B22234] focus:ring-0"
                      />
                    </div>
                    <div>
                      <Label htmlFor="branch" className="font-condensed uppercase tracking-widest text-xs text-[#0A3161]">
                        Branch of Service
                      </Label>
                      <Input
                        id="branch"
                        value={form.branch}
                        onChange={(e) => setForm({ ...form, branch: e.target.value })}
                        placeholder="Army, Navy, Marines, Air Force, Coast Guard…"
                        className="mt-2 h-12 rounded-none border-[#D9DDE3] focus:border-[#B22234] focus:ring-0"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="font-condensed uppercase tracking-widest text-xs text-[#0A3161]">
                      Membership Type
                    </Label>
                    <RadioGroup
                      value={form.memberType}
                      onValueChange={(v) => setForm({ ...form, memberType: v })}
                      className="mt-3 grid sm:grid-cols-3 gap-3"
                    >
                      {[
                        { v: "veteran", l: "Vietnam Era Veteran" },
                        { v: "other", l: "Other Era Veteran" },
                        { v: "associate", l: "Associate / Supporter" },
                      ].map((o) => (
                        <label
                          key={o.v}
                          className={`cursor-pointer border p-4 flex items-center gap-3 transition-colors ${
                            form.memberType === o.v
                              ? "border-[#B22234] bg-[#EEF1F6]"
                              : "border-[#D9DDE3] hover:border-[#B22234]/40"
                          }`}
                        >
                          <RadioGroupItem value={o.v} id={o.v} className="border-[#B22234] text-[#B22234]" />
                          <span className="text-sm font-condensed uppercase tracking-wide text-[#0A3161]">
                            {o.l}
                          </span>
                        </label>
                      ))}
                    </RadioGroup>
                  </div>

                  <div>
                    <Label htmlFor="message" className="font-condensed uppercase tracking-widest text-xs text-[#0A3161]">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={4}
                      placeholder="Tell us a little about your service and interest in joining…"
                      className="mt-2 rounded-none border-[#D9DDE3] focus:border-[#B22234] focus:ring-0"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#B22234] hover:bg-[#8f1b2a] text-white rounded-none font-condensed uppercase tracking-widest text-sm h-14"
                  >
                    Submit Application
                  </Button>

                  <div className="pt-4 border-t border-[#D9DDE3] flex flex-col sm:flex-row gap-3 text-sm text-[#1F2937]">
                    <a href="tel:8885480085" className="flex items-center gap-2 hover:text-[#B22234]">
                      <Phone className="w-4 h-4" /> 888-548-0085
                    </a>
                    <a href="mailto:info@vva240.org" className="flex items-center gap-2 hover:text-[#B22234]">
                      <Mail className="w-4 h-4" /> info@vva240.org
                    </a>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
